import { useState } from "react";
import { PlayerRotationChartProps } from "./PlayerRotationChart/types";
import "../teamReportDesign.scss";

// Stint cutoffs (user call 2026-08-28): solid ≥ 65% of games on floor at
// that minute, grey ≥ 30%, else empty track.
const STINT_SOLID = 0.65;
const STINT_GREY = 0.3;

const ridgePath = (mins: number[]) => {
  const step = 480 / (mins.length - 1 || 1);
  return (
    "M0,16 " +
    mins
      .map((v, i) => `L${(i * step).toFixed(1)},${(15.5 - 14.5 * v).toFixed(1)}`)
      .join(" ") +
    " L480,16 Z"
  );
};

// Game blobs carry one entry per minute played: 48, plus five per overtime.
const REGULATION_MINUTES = 48;
const OT_MINUTES = 5;
const otLabel = (k: number) => (k === 0 ? "OT" : `${k + 1}OT`);

/**
 * The 1D/2A rotation map: per-player 48-minute floor-share tracks with
 * quarter rules and a Last-5 games column. Two modes — Stints (solid/grey
 * threshold cells) and Density (filled ridge profile) — toggled on the web;
 * print renders whatever mode is selected (the same DOM prints). A game
 * that went to overtime widens the track by five-minute OT columns (a period
 * keeps its true share of the width); a regulation game renders exactly the
 * 48-minute markup.
 */
export default function RotationMap({
  players,
  isSingleGame = false,
  marker,
}: PlayerRotationChartProps & {
  /** A moment to mark across every track (game minutes elapsed, on the same
   * scale as the columns — overtimes count five each), e.g. garbage time */
  marker?: { minute: number; label: string };
}) {
  const [mode, setMode] = useState<"stints" | "density">("stints");
  const rowClass = isSingleGame ? "rrow nol5" : "rrow";
  const longest = Math.max(
    REGULATION_MINUTES,
    ...players.map((p) => p.rotationMinutes?.length ?? 0),
  );
  const overtimes = Math.ceil((longest - REGULATION_MINUTES) / OT_MINUTES);
  const totalMinutes = REGULATION_MINUTES + overtimes * OT_MINUTES;
  // Overtime only: explicit column widths and rule positions (regulation keeps
  // the stylesheet's four equal quarters and 25/50/75% rules).
  const otColumns = overtimes
    ? { gridTemplateColumns: `repeat(${totalMinutes}, 1fr)` }
    : undefined;
  const ruleAt = (minute: number) =>
    overtimes ? { left: `${(minute / totalMinutes) * 100}%` } : undefined;
  const markerAt = marker
    ? { left: `${(marker.minute / totalMinutes) * 100}%` }
    : undefined;
  const rules = (
    <>
      <i className="q2" style={ruleAt(12)} />
      <i className="q3" style={ruleAt(24)} />
      <i className="q4" style={ruleAt(36)} />
      {Array.from({ length: overtimes }, (_, k) => (
        <i
          key={k}
          className="ot"
          style={ruleAt(REGULATION_MINUTES + k * OT_MINUTES)}
        />
      ))}
      {marker && <i className="mk" style={markerAt} />}
    </>
  );

  return (
    <div className="trd">
      <div className="rot">
        <div className="sh pg" style={{ marginTop: 0 }}>
          <span className="ttl">Rotation</span>
          <span className="nt">
            {mode === "stints"
              ? `Stints — solid ≥ ${Math.round(STINT_SOLID * 100)}% · grey ≥ ${Math.round(STINT_GREY * 100)}%`
              : "Density — filled floor-share profile"}
            {marker && (
              <>
                {" · "}
                <span className="mkkey" />
                {marker.label}
              </>
            )}
          </span>
          <span className="modes trd-webonly" style={{ display: "inline-flex" }}>
            <button
              type="button"
              className={mode === "stints" ? "on" : ""}
              onClick={() => setMode("stints")}
            >
              Stints
            </button>
            <button
              type="button"
              className={mode === "density" ? "on" : ""}
              onClick={() => setMode("density")}
            >
              Density
            </button>
          </span>
        </div>
        <div className={`${rowClass} rh`}>
          {/* split so MPG sits over the right-aligned MPG values */}
          <span
            style={{ display: "flex", justifyContent: "space-between", gap: 4 }}
          >
            <span>Player</span>
            <span>MPG</span>
          </span>
          {!isSingleGame && <span className="c">Last 5</span>}
          <span
            className="qs"
            style={
              overtimes
                ? {
                    gridTemplateColumns: `repeat(4, 12fr) repeat(${overtimes}, 5fr)`,
                  }
                : undefined
            }
          >
            <span>Q1</span>
            <span>Q2</span>
            <span>Q3</span>
            <span>Q4</span>
            {Array.from({ length: overtimes }, (_, k) => (
              <span key={k}>{otLabel(k)}</span>
            ))}
          </span>
        </div>
        {players.map(({ playerId, playerName, rotationMinutes, last5Minutes }) => {
          const mins: number[] = Array.from(rotationMinutes ?? []);
          // A player whose array stops short (no OT entries) sits out the rest
          while (mins.length < totalMinutes) mins.push(0);
          const mpg = mins.reduce((a, b) => a + (b || 0), 0);
          return (
            <div className={rowClass} key={playerId}>
              <span className="rname">
                <span>{playerName.toUpperCase()}</span>
                <span className="mp">{mpg.toFixed(1)}</span>
              </span>
              {!isSingleGame && (
                <span className="l5">
                  {(last5Minutes ?? []).map((v, i) => (
                    <span
                      key={i}
                      style={{ background: `rgba(26,26,26,${(v || 0).toFixed(2)})` }}
                    />
                  ))}
                </span>
              )}
              {mode === "stints" ? (
                <span className="trk" style={otColumns}>
                  {mins.map((v, i) => (
                    <span
                      key={i}
                      style={{
                        background:
                          v >= STINT_SOLID
                            ? "#1a1a1a"
                            : v >= STINT_GREY
                              ? "#c9c6c2"
                              : "transparent",
                      }}
                    />
                  ))}
                  {rules}
                </span>
              ) : (
                <span className="ridgewrap">
                  <svg
                    className="ridge"
                    viewBox="0 0 480 16"
                    preserveAspectRatio="none"
                  >
                    <path d={ridgePath(mins)} fill="#1a1a1a" opacity="0.82" />
                  </svg>
                  {rules}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
